<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :modal-append-to-body="false"
    :before-close="cancel"
    :title="
      isCreate ? $t('Gamification_Create_Prize') : $t('Gamification_Edit_Prize')
    "
    :close-on-click-modal="false"
    @click.native.stop
    destroy-on-close
  >
    <div
      v-loading="isLoading"
      ref="dialogBody"
      data-qa="prize-form-dialog-wrapper"
    >
      <rest-form
        v-if="model"
        ref="prize-form"
        :model="model"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :rules="rules"
        class="game-step-prizes-form"
        @on-error="onError"
      >
        <form-section
          :title="$t('Gamification_Name_Prize')"
          :description="$t('Gamification_Name_Prize_Description')"
          :is-required="true"
          data-qa="section-prize-name"
        >
          <rest-form-item
            prop="name"
            :label="$t('Gamification_Name_Prize')"
            class="default-width"
          >
            <el-input
              v-model="model.name"
              required
              data-qa="input-prize-name"
            />
          </rest-form-item>
        </form-section>

        <form-section
          v-if="isLeaderboard"
          :title="$t('Gamification_Leaderboard_Rank')"
          :description="$t('Gamification_Leaderboard_Rank_Description')"
          :is-required="true"
          data-qa="section-rank"
        >
          <div class="form-field-rank">
            <rest-form-item prop="range_from" :label="$t('Gamification_Min')">
              <el-input-number
                controls-position="right"
                v-model="model.range_from"
                :min="0"
                data-qa="input-rank-min"
              />
            </rest-form-item>

            <rest-form-item prop="range_to" :label="$t('Gamification_Max')">
              <el-input-number
                controls-position="right"
                v-model="model.range_to"
                :min="1"
                data-qa="input-rank-max"
              />
            </rest-form-item>
          </div>
        </form-section>

        <form-section
          v-else
          :title="$t('Gamification_Points')"
          :description="$t('Gamification_Points_Description')"
          :is-required="false"
          data-qa="section-points"
        >
          <div class="form-field-rank">
            <rest-form-item prop="range_from" :label="$t('Gamification_Min')">
              <el-input-number
                controls-position="right"
                v-model="model.range_from"
                :min="0"
                data-qa="input-points-min"
              />
            </rest-form-item>

            <rest-form-item prop="range_to" :label="$t('Gamification_Max')">
              <el-input-number
                controls-position="right"
                v-model="model.range_to"
                :min="1"
                data-qa="input-points-max"
              />
            </rest-form-item>
          </div>
        </form-section>
        <form-section
          :title="$t('Gamification_Segment')"
          :description="$t('Gamification_Segment_Description')"
          :is-required="true"
          data-qa="section-segment"
        >
          <multiple-segment-select
            prop="customer_segment_ids"
            v-model="model.customer_segment_ids"
            :label="$t('Gamification_Segment')"
            clearable
            multiple
            class="default-width"
            data-qa="select-segment"
          />
        </form-section>

        <form-section
          :title="$t('Gamification_Start_End_Date')"
          :description="$t('Gamification_Start_End_Date_Prize_Description')"
          :is-required="true"
          data-qa="section-dates"
        >
          <div class="default-width date-time-content">
            <rest-form-item
              prop="starts_at"
              :label="$t('Gamification_Start_Date')"
              class="flex-1"
            >
              <el-date-picker
                v-model="model.starts_at"
                type="datetime"
                format="MMMM d yyyy HH:mm"
                :picker-options="startPickerOptions"
                default-time="00:00:00"
                required
                data-qa="input-start-date"
                @change="onDateChange($event, 'starts_at', 'ends_at')"
              />
            </rest-form-item>

            <rest-form-item
              prop="ends_at"
              :label="$t('Gamification_End_Date')"
              class="flex-1"
            >
              <el-date-picker
                v-model="model.ends_at"
                type="datetime"
                format="MMMM d yyyy HH:mm"
                :picker-options="endPickerOptions"
                default-time="23:59:59"
                required
                data-qa="input-end-date"
                @change="onDateChange($event, 'ends_at', 'starts_at')"
              />
            </rest-form-item>
          </div>
        </form-section>

        <form-section
          :title="$t('Gamification_Promotion')"
          :description="$t('Gamification_Promotion_Description')"
          v-model="model.promotion_name"
          data-qa="section-promotion"
        >
          <promotion-setup
            v-model="model.promotion_attributes"
            data-qa="promotion-setup"
          />
        </form-section>
      </rest-form>

      <div class="footer" data-qa="prize-form-footer">
        <div class="footer-content">
          <el-button type="primary" @click="cancel" plain data-qa="cancel-btn">
            {{ $t('Gamification_Cancel') }}
          </el-button>
          <el-button
            type="primary"
            :disabled="isSaveDisabled"
            @click="save"
            data-qa="save-btn"
          >
            {{ $t('Gamification_Save') }}
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormSection from '@/common/components/form/FormSection'
import MultipleSegmentSelect from '@/modules/gamification/components/games-campaigns/MultipleSegmentSelect.vue'
import PromotionSetup from '@/modules/campaign-manage/components/setup/PromotionSetup'
import { checkIsButtonDisabled, castDate } from '@/modules/gamification/helpers'
import { projectEndpoint } from '@/core/helpers'

export default {
  name: 'GameStepPrizesFormDialog',
  components: {
    RestForm,
    RestFormItem,
    FormSection,
    MultipleSegmentSelect,
    PromotionSetup
  },
  data() {
    return {
      requiredFields: ['name', 'customer_segment_ids', 'starts_at', 'ends_at'],
      isLoading: false,
      initialStateModel: undefined
    }
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    isCreate: {
      type: Boolean,
      required: true
    },
    dialogVisible: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    isLeaderboard: {
      type: Boolean
    },
    defaultSegmentId: {
      type: Number,
      default: null
    }
  },

  mounted() {
    this.initialStateModel = _.cloneDeep(this.model)
  },

  computed: {
    rules() {
      return {
        name: {
          required: true,
          message: this.$t('Gamification_GameStepPrizesForm_PrizeNameRequired'),
          trigger: 'blur'
        },
        customer_segment_ids: {
          required: true,
          message: this.$t('Gamification_GameStepPrizesForm_SegmentRequired'),
          trigger: 'blur'
        },
        starts_at: [
          {
            required: true,
            message: this.$t('Gamification_GameBasicSetupForm_StartAtRequired'),
            trigger: ['blur', 'change']
          },
          {
            validator: (_rule, value, callback) => {
              const endsAt = this.model.ends_at
              if (!value || !endsAt) {
                return callback()
              }

              if (moment(value).isSameOrAfter(moment(endsAt))) {
                return callback(
                  new Error(this.$t('Gamification_StartTimeValidation'))
                )
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ],
        ends_at: [
          {
            required: true,
            message: this.$t('Gamification_GameStepPrizesForm_EndAtRequired'),
            trigger: ['blur', 'change']
          },
          {
            validator: (_rule, value, callback) => {
              const startsAt = this.model.starts_at

              if (!value || !startsAt) return callback()

              if (moment(value).isSameOrBefore(moment(startsAt))) {
                return callback(
                  new Error(this.$t('Gamification_EndTimeValidation'))
                )
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ]
      }
    },

    hasChanges() {
      return !_.isEqual(this.model, this.initialStateModel)
    },
    startPickerOptions() {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const now = moment().startOf('day')
          const current = moment(date).startOf('day')

          if (current.isBefore(now, 'day')) return true

          if (this.model.ends_at) {
            return current.isAfter(moment(this.model.ends_at).startOf('day'))
          }

          return false
        }
      }
    },

    isSaveDisabled() {
      return checkIsButtonDisabled(this.requiredFields, this.model)
    },

    endPickerOptions() {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const now = moment().startOf('day')
          const current = moment(date).startOf('day')

          if (current.isBefore(now, 'day')) return true

          if (this.model.starts_at) {
            return current.isBefore(moment(this.model.starts_at).startOf('day'))
          }

          return false
        }
      }
    },

    saveEndpoint() {
      if (this.model.id) {
        return projectEndpoint({
          method: 'put',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/prize/${this.model.id}`
        })
      }
      return projectEndpoint({
        method: 'post',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/prize`
      })
    }
  },

  watch: {
    dialogVisible: {
      handler(newVal) {
        if (
          newVal &&
          this.defaultSegmentId &&
          this.model &&
          this.model.customer_segment_ids &&
          !this.model.customer_segment_ids.length
        ) {
          this.model.customer_segment_ids.push(this.defaultSegmentId)
        }
      },
      immediate: true
    }
  },

  methods: {
    async save() {
      this.isLoading = true
      try {
        const valid = await this.$refs['prize-form'].validate()

        if (valid) {
          this.$refs['prize-form'].save()
        }
      } catch (e) {
        console.error(e)
        this.isLoading = false
      }
    },

    afterSave(data, finishLoading) {
      finishLoading()
      this.$emit('refetch')
      this.isLoading = false
      this.$emit('close')
    },

    cancel() {
      this.$emit('close')
    },

    onError() {
      this.isLoading = false
    },

    revalidateField(field) {
      if (!this.model || !this.model[field]) return

      this.$refs['prize-form'].validateField(field)
    },

    onDateChange(payload, modelField, revalidateField) {
      this.model[modelField] = castDate(payload)
      this.revalidateField(revalidateField)
    }
  }
}
</script>
<style scoped>
>>> .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

>>> .el-dialog__body {
  height: 650px;
  overflow: auto;
}

.game-step-prizes-form {
  padding-bottom: 20px;
}

.form-field-rank {
  display: flex;
  gap: 25px;
}

.footer {
  width: 100%;
  background: #ffffff;
}

.footer-content {
  display: flex;
  gap: 35px;
  justify-content: end;
  margin: 20px;
}

.date-time-content {
  display: flex;
  gap: 24px;
  align-items: center;
}

.default-width {
  width: 553px;
}

.flex-1 {
  flex: 1;
}

>>> .el-form-item__error {
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
}

@media screen and (max-height: 950px) {
  >>> .el-dialog__body {
    height: 550px;
  }
}

@media screen and (max-height: 850px) {
  >>> .el-dialog__body {
    height: 450px;
  }
}

@media screen and (max-height: 650px) {
  >>> .el-dialog__body {
    height: 250px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/prizes/GameStepPrizesFormDialog.vue